import mixpanel from "mixpanel-browser";
import { logger } from "@/lib/logger";

const LOG_NAMESPACE = "ANALYTICS";
const MIXPANEL_ENABLED_FLAG = import.meta.env.VITE_MIXPANEL_ENABLED;
const mixpanelEnabledByEnv = MIXPANEL_ENABLED_FLAG?.toLowerCase() === "true";

export const isMixpanelTrackingEnabled = () => mixpanelEnabledByEnv;

declare global {
  interface Window {
    gtag: (
      command: "event",
      action: string,
      params?: Record<string, unknown>,
    ) => void;
  }
}

/**
 * Location-like shape required for page metadata derivation.
 */
type LocationSnapshot = Pick<
  Location,
  "pathname" | "search" | "href" | "hash" | "origin"
>;

/**
 * Document-like shape required for page metadata derivation.
 */
type DocumentSnapshot = Pick<Document, "referrer">;

/**
 * Canonical set of page metadata fields sent with Mixpanel page view events.
 */
export interface PageMetadata {
  page: string;
  page_url: string;
  page_title: string;
  referrer?: string;
}

/**
 * Lightweight overrides for canonical page metadata.
 */
export type PageMetadataOverrides = Partial<PageMetadata>;

/**
 * Creates canonical page metadata for Mixpanel tracking using the provided location context.
 *
 * @param overrides - Optional metadata overrides to apply on top of derived values.
 * @param context - Optional context containing location and document snapshots.
 * @returns Canonical metadata describing the current page.
 */
export const createPageMetadata = (
  overrides: PageMetadataOverrides = {},
  context: {
    location?: LocationSnapshot;
    documentRef?: DocumentSnapshot;
  } = {},
): PageMetadata => {
  const defaultLocation: LocationSnapshot | undefined =
    typeof window !== "undefined" && window.location
      ? {
          pathname: window.location.pathname,
          search: window.location.search,
          href: window.location.href,
          hash: window.location.hash,
          origin: window.location.origin,
        }
      : undefined;

  const location = context.location ??
    defaultLocation ?? {
      pathname: "/",
      search: "",
      href: "/",
      hash: "",
      origin: "",
    };

  const defaultDocument: DocumentSnapshot | undefined =
    typeof document !== "undefined"
      ? { referrer: document.referrer }
      : undefined;

  const documentRef = context.documentRef ??
    defaultDocument ?? { referrer: "" };

  const pathname = location.pathname || "/";
  const pageValue = overrides.page ?? pathname;
  const canonicalTitle =
    pathname === "/" ? "home" : pathname.replace(/^\//, "") || "home";
  const pageTitleValue = overrides.page_title ?? canonicalTitle;

  const hasOrigin = Boolean(location.origin && location.origin !== "null");
  const absoluteUrl = hasOrigin
    ? `${location.origin}${pathname}${location.search}${location.hash ?? ""}`
    : location.href || `${pathname}${location.search}${location.hash ?? ""}`;
  const pageUrlValue = overrides.page_url ?? absoluteUrl;

  const referrerValue =
    overrides.referrer ??
    (documentRef.referrer ? documentRef.referrer : undefined);

  return {
    page: pageValue,
    page_url: pageUrlValue,
    page_title: pageTitleValue,
    ...(referrerValue ? { referrer: referrerValue } : {}),
  };
};

// Mixpanel user identification
export const identifyUser = (
  userId: string,
  userProperties?: {
    email?: string;
    name?: string;
    [key: string]: unknown;
  },
) => {
  if (!isMixpanelTrackingEnabled()) {
    return;
  }

  mixpanel.identify(userId);
  if (userProperties) {
    mixpanel.people.set({
      $name: userProperties.name,
      $email: userProperties.email,
      ...userProperties,
    });
  }
};

export const resetMixpanelUser = () => {
  if (!isMixpanelTrackingEnabled()) {
    return;
  }

  mixpanel.reset();
};

// Mixpanel event tracking
export const trackMixpanelEvent = (
  eventName: string,
  properties?: Record<string, unknown>,
) => {
  if (!isMixpanelTrackingEnabled()) {
    return;
  }

  const cleaned = properties
    ? Object.fromEntries(
        Object.entries(properties).filter(([, v]) => v !== undefined),
      )
    : undefined;
  mixpanel.track(eventName, cleaned);
};

export const trackEvent = (
  action: string,
  params?: Record<string, unknown>,
) => {
  if (window.gtag) {
    window.gtag("event", action, params);
  } else {
    logger.warn(LOG_NAMESPACE, "Google Analytics gtag function not found");
  }
};

export const trackCompanySelection = (company: {
  id: string;
  symbol?: string | null;
  name: string;
}) => {
  // Converted to Mixpanel - keeping for backward compatibility
  trackMixpanelEvent("Company Selection", {
    company_id: company.id,
    company_name: company.name,
    company_symbol: company.symbol,
    page: window.location.pathname,
  });
};

export const trackScanRequest = (company: {
  id: string;
  symbol?: string | null;
  name: string;
}) => {
  // Converted to Mixpanel - keeping for backward compatibility
  trackMixpanelEvent("Scan Request", {
    company_id: company.id,
    company_name: company.name,
    company_symbol: company.symbol,
    page: window.location.pathname,
  });
};

/**
 * Properties accepted when tracking page view events.
 */
export interface PageViewProperties extends Record<string, unknown> {
  page?: string;
  page_url?: string;
  page_title?: string;
  referrer?: string;
}

/**
 * Tracks a page view event in Mixpanel with canonical metadata.
 *
 * @param properties - Optional properties to include with the page view event.
 */
export const trackPageView = (properties: PageViewProperties = {}) => {
  const { page, page_title, page_url, referrer, ...rest } = properties;
  const metadata = createPageMetadata({ page, page_title, page_url, referrer });
  trackMixpanelEvent("Page View", { ...metadata, ...rest });
};

export const trackError = (properties: {
  error_type: string;
  error_message: string;
  error_code?: string;
  page_url: string;
  user_id?: string;
}) => {
  trackMixpanelEvent("Error", properties);
};

export const trackPurchase = (properties: {
  user_id: string;
  transaction_id: string;
  revenue: number;
  currency: string;
}) => {
  trackMixpanelEvent("Purchase", properties);
};

export const trackConversion = (properties: {
  "Conversion Type": string;
  "Conversion Value"?: number;
}) => {
  trackMixpanelEvent("Conversion", properties);
};

// New Mixpanel event tracking functions
export const trackContactUsClick = (properties: {
  page: string;
  page_url?: string;
}) => {
  trackMixpanelEvent("Contact Us Click", properties);
};

export const trackExploreButtonClick = (properties: {
  page: string;
  selected_company_id?: string;
  selected_company_name?: string;
  selected_company_symbol?: string | null;
  selected_map_id?: string;
  selected_map_name?: string;
}) => {
  trackMixpanelEvent("Explore Button Click", properties);
};

export const trackMapSelection = (properties: {
  page: string;
  map_id: string;
  map_name: string;
}) => {
  trackMixpanelEvent("Map Selection", properties);
};

export const trackCompanyClick = (properties: {
  company_id: string;
  company_name: string;
  company_symbol?: string | null;
  page: string;
}) => {
  trackMixpanelEvent("Company Click", properties);
};

export const trackRelationshipToggle = (properties: {
  relationship_id: string;
  relationship_type: string;
  action: "show" | "hide";
  entity_id: string;
  entity_name: string;
  page: string;
}) => {
  trackMixpanelEvent("Relationship Toggle", properties);
};

export const trackFilterClick = (properties: {
  filter_type: "relationship_type" | "entity";
  filter_value: string;
  action: "add" | "remove";
  entity_id: string;
  entity_name: string;
  page: string;
}) => {
  trackMixpanelEvent("Filter Click", properties);
};

export const trackNotebookButtonClick = (properties: { page: string }) => {
  trackMixpanelEvent("Notebook Button Click", properties);
};

export const trackJoinBetaClick = (properties: {
  page: string;
  page_url?: string;
}) => {
  trackMixpanelEvent("Join Beta Click", properties);
};

export const trackWhatAmISeeingClick = (properties: {
  page: string;
  page_url?: string;
}) => {
  trackMixpanelEvent("What Am I Seeing Click", properties);
};

export const trackDataSourceAdd = (properties: {
  data_source_count: number;
  company_ids: string[];
  company_names: string[];
}) => {
  trackMixpanelEvent("Data Source Add", properties);
};

export const trackDataSourceRemove = (properties: {
  data_source_id: string;
  company_id?: string;
  company_name?: string;
  action: "remove_one" | "clear_all";
  remaining_count: number;
}) => {
  trackMixpanelEvent("Data Source Remove", properties);
};

// Form submission tracking
export const trackFormSubmit = (properties: {
  form_name: string;
  form_type: "contact" | "betaRegistration" | "notebook_access" | "auth_modal";
  page: string;
  page_url?: string;
  topic?: string; // For contact form
}) => {
  trackMixpanelEvent("Form Submit", properties);
};

export const trackFormSuccess = (properties: {
  form_name: string;
  form_type: "contact" | "betaRegistration" | "notebook_access" | "auth_modal";
  page: string;
  page_url?: string;
}) => {
  trackMixpanelEvent("Form Success", properties);
};

export const trackFormError = (properties: {
  form_name: string;
  form_type: "contact" | "betaRegistration" | "notebook_access" | "auth_modal";
  error_message: string;
  page: string;
  page_url?: string;
}) => {
  trackMixpanelEvent("Form Error", properties);
};

export const trackProjectCreated = (properties: {
  project_id: string;
  source: "auto" | "manual";
}) => {
  trackMixpanelEvent("Project Created", properties);
};

export const trackProjectOpened = (properties: {
  project_id: string;
  project_name?: string;
}) => {
  trackMixpanelEvent("Project Opened", properties);
};

export const trackProjectRenamed = (properties: {
  project_id: string;
  previous_name: string;
  new_name: string;
}) => {
  trackMixpanelEvent("Project Renamed", properties);
};

export const trackArtifactCreated = (properties: {
  artifact_id: string;
  project_id: string;
  artifact_type: string;
  source: "empty" | "document" | "portfolio" | "duplicate";
}) => {
  trackMixpanelEvent("Artifact Created", properties);
};

export const trackArtifactSelected = (properties: {
  artifact_id: string;
  artifact_type: string;
  project_id?: string;
}) => {
  trackMixpanelEvent("Artifact Selected", properties);
};

export const trackArtifactRenamed = (properties: {
  artifact_id: string;
  project_id: string;
  artifact_type: string;
  previous_name: string;
  new_name: string;
}) => {
  trackMixpanelEvent("Artifact Renamed", properties);
};

export const trackArtifactDeleted = (properties: {
  artifact_id: string;
  project_id: string;
  artifact_type: string;
}) => {
  trackMixpanelEvent("Artifact Deleted", properties);
};

export const trackArtifactDuplicated = (properties: {
  artifact_id: string;
  project_id: string;
  artifact_type: string;
  new_artifact_id: string;
}) => {
  trackMixpanelEvent("Artifact Duplicated", properties);
};

export const trackLayerAdded = (properties: {
  layer_id: string;
  layer_type: string;
  map_id?: string | null;
  source: "empty-map" | "layers-panel";
}) => {
  trackMixpanelEvent("Layer Added", properties);
};

export const trackLayerRemoved = (properties: {
  layer_id: string;
  layer_type: string;
  map_id?: string | null;
  remaining_count: number;
}) => {
  trackMixpanelEvent("Layer Removed", properties);
};

export const trackLayerUpdated = (properties: {
  layer_id: string;
  layer_type: string;
  map_id?: string | null;
  changed_fields: string[];
}) => {
  trackMixpanelEvent("Layer Updated", properties);
};

export const trackEmergentLayerUpdated = (properties: {
  map_id?: string | null;
  changed_fields: string[];
}) => {
  trackMixpanelEvent("Emergent Layer Updated", properties);
};

export const trackLayersCleared = (properties: {
  map_id?: string | null;
  layer_count: number;
}) => {
  trackMixpanelEvent("Layers Cleared", properties);
};

export const trackLayersPanelToggled = (properties: { is_open: boolean }) => {
  trackMixpanelEvent("Layers Panel Toggled", properties);
};

export const trackLayoutPanelToggled = (properties: { is_open: boolean }) => {
  trackMixpanelEvent("Layout Panel Toggled", properties);
};

export const trackDetailsPanelToggled = (properties: {
  is_open: boolean;
  source: "desktop" | "mobile";
  trigger: "manual" | "auto";
}) => {
  trackMixpanelEvent("Details Panel Toggled", properties);
};

export const trackLayoutSettingChanged = (properties: {
  setting: "cluster_by" | "cluster_strength" | "show_cluster_labels";
  value: string | number | boolean;
}) => {
  trackMixpanelEvent("Layout Setting Changed", properties);
};

export const trackChatButtonClicked = (properties: {
  source: "desktop" | "mobile";
  trigger: "manual";
  label?: string;
}) => {
  trackMixpanelEvent("Chat Button Clicked", properties);
};

export const trackShareDialogOpened = (properties: {
  map_id?: string | null;
}) => {
  trackMixpanelEvent("Share Dialog Opened", properties);
};

export const trackShareSnapshotCreated = (properties: {
  map_id: string;
  snapshot_id: string;
}) => {
  trackMixpanelEvent("Share Snapshot Created", properties);
};

export const trackShareLinkCopied = (properties: { map_id: string }) => {
  trackMixpanelEvent("Share Link Copied", properties);
};

export const trackShareChannelClicked = (properties: {
  map_id: string;
  channel: "twitter" | "whatsapp";
}) => {
  trackMixpanelEvent("Share Channel Clicked", properties);
};

export const trackConnectorDialogOpened = () => {
  trackMixpanelEvent("Connector Dialog Opened");
};

export const trackConnectorAction = (properties: {
  action: "register" | "connect" | "disconnect" | "add_account";
  provider: "snaptrade";
}) => {
  trackMixpanelEvent("Connector Action", properties);
};

export const trackConnectorStatusFetched = (properties: {
  provider: "snaptrade";
  status: string;
}) => {
  trackMixpanelEvent("Connector Status Fetched", properties);
};

export const trackScanRequested = (properties: {
  entity_id: string;
  entity_name: string;
  entity_source: string;
}) => {
  trackMixpanelEvent("Manual Scan Requested", properties);
};

export const trackScanCompleted = (properties: {
  entity_id: string;
  entity_name: string;
  status: "completed" | "failed";
}) => {
  trackMixpanelEvent("Manual Scan Completed", properties);
};

export const trackGraphNodeClicked = (properties: {
  node_id: string;
  node_type: string;
  node_name?: string;
}) => {
  trackMixpanelEvent("Graph Node Clicked", properties);
};

export const trackChartOpened = (properties: {
  symbol: string;
  company_name: string;
}) => {
  trackMixpanelEvent("Chart Opened", properties);
};

export const trackSearchPerformed = (properties: {
  search_query: string;
  results_count: number;
  context: "graph-search" | "empty-map";
}) => {
  trackMixpanelEvent("Search Performed", properties);
};

export const trackTrailSelected = (properties: {
  trail_id: string;
  trail_type: string;
  source_entity_id: string;
  target_entity_id: string;
  context: "trails-list" | "graph";
}) => {
  trackMixpanelEvent("Trail Selected", properties);
};

export const trackAuthSuccess = (properties: {
  flow: "signup" | "login";
  provider: "google" | "linkedin" | "email-otp";
  is_new_user?: boolean;
  user_id: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  referrer?: string;
}) => trackMixpanelEvent("Auth Success", properties);

export const trackSharedArtifactViewed = (properties: {
  artifact_id: string;
  channel: string;
  is_authenticated: boolean;
  utm_source?: string;
}) => trackMixpanelEvent("Shared Artifact Viewed", properties);

export const trackChatMessageSent = (properties: {
  source: "mobile" | "desktop";
  input_length: number;
  artifact_id?: string;
  project_id?: string;
  session_id: string;
}) => trackMixpanelEvent("Chat Message Sent", properties);

export const trackChatResponseReceived = (properties: {
  success: boolean;
  latency_ms: number;
  finish_reason?: string;
  error_type?: string;
  session_id: string;
}) => trackMixpanelEvent("Chat Response Received", properties);

export const trackSearchResultClicked = (properties: {
  search_query: string;
  result_type: string;
  result_id: string;
  result_name: string;
  position: number;
  results_count: number;
}) => trackMixpanelEvent("Search Result Clicked", properties);

export const trackDocumentGenerationStarted = (properties: {
  artifact_id: string;
  artifact_type: string;
  project_id?: string;
}) => trackMixpanelEvent("Document Generation Started", properties);

export const trackMapPopulationStarted = (properties: {
  artifact_id: string;
  project_id: string;
  source: "company" | "preset" | "portfolio" | "scratch";
}) => trackMixpanelEvent("Map Population Started", properties);

export const trackMapPopulationCompleted = (properties: {
  artifact_id: string;
  project_id: string;
  source: "company" | "preset" | "portfolio" | "scratch";
  success: boolean;
  duration_ms: number;
  error_type?: string;
}) => trackMixpanelEvent("Map Population Completed", properties);

export const trackDocumentGenerationCompleted = (properties: {
  artifact_id: string;
  artifact_type: string;
  project_id?: string;
  success: boolean;
  duration_ms: number;
  error_type?: string;
}) => trackMixpanelEvent("Document Generation Completed", properties);

export const trackMobileChatInputClicked = (properties: {
  context: "map-inline" | "document-inline" | "chat-drawer";
}) => trackMixpanelEvent("Mobile Chat Input Clicked", properties);

export const trackMobileChatQuickActionClicked = (properties: {
  action_label: string;
  action_prompt?: string;
  context: "map-inline" | "document-inline";
}) => trackMixpanelEvent("Mobile Chat Quick Action Clicked", properties);

export const trackMobileCreateMapClicked = (properties: {
  context: "header-cta" | "create-artifact-sheet";
  project_id?: string;
}) => trackMixpanelEvent("Mobile Create Map Clicked", properties);

export const trackMobileOverviewCtaClicked = (properties: {
  trigger: "top-right";
  project_id?: string;
  artifact_id?: string;
}) => trackMixpanelEvent("Mobile Overview CTA Clicked", properties);
